// Pure helpers for the texting-move PIN (the one-time 6-digit code a
// consultant enters so her registration with the new texting provider can
// finish). Kept free of React so they can be unit tested on their own.

export const PIN_LENGTH = 6;

// Strip everything that is not a digit and cap at 6. Handles a paste of the
// whole text ("Your code is 123 456.") as well as typing.
export const sanitizePin = (value) => String(value ?? '')
  .replace(/\D/g, '')
  .slice(0, PIN_LENGTH);

export const isValidPin = (value) => typeof value === 'string'
  && new RegExp(`^\\d{${PIN_LENGTH}}$`).test(value);

// Whole seconds from nowMs until an ISO timestamp (or epoch ms). 0 when the
// target is missing, unparseable or already past.
export const secondsUntil = (target, nowMs = Date.now()) => {
  if (target === null || target === undefined || target === '') {
    return 0;
  }
  const targetMs = typeof target === 'number' ? target : Date.parse(target);
  if (Number.isNaN(targetMs)) {
    return 0;
  }
  const remaining = Math.ceil((targetMs - nowMs) / 1000);
  return remaining > 0 ? remaining : 0;
};

// Compact countdown text: "45s", "2m 5s", "3h 10m". A throttled resend can
// be told to wait hours, and "10800s" is not readable.
export const formatCooldown = (seconds) => {
  const total = Math.max(0, Math.ceil(Number(seconds) || 0));
  if (total < 60) {
    return `${total}s`;
  }
  if (total < 3600) {
    const m = Math.floor(total / 60);
    const s = total % 60;
    return s ? `${m}m ${s}s` : `${m}m`;
  }
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  return m ? `${h}h ${m}m` : `${h}h`;
};

// canResendAt from the server (ISO string or null) as epoch ms, or null.
export const toEpochMs = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const ms = typeof value === 'number' ? value : Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
};

// "Remind me later" on the PIN modal hides it for this browser session
// only. Keyed by user so a different consultant signing in on the same tab
// still sees her own prompt. sessionStorage can throw (private mode, blocked
// storage) -- every access is wrapped, and failure means the modal simply
// shows again, which is the safe direction.
const REMIND_LATER_KEY_PREFIX = 'cp.telnyxMigrationPin.remindLater';

const getRemindLaterKey = (userId) => `${REMIND_LATER_KEY_PREFIX}:${userId ?? 'unknown'}`;

export const readPinRemindLater = (userId) => {
  try {
    return sessionStorage.getItem(getRemindLaterKey(userId)) === '1';
  } catch (e) {
    return false;
  }
};

export const writePinRemindLater = (userId) => {
  try {
    sessionStorage.setItem(getRemindLaterKey(userId), '1');
  } catch (e) {
    // sessionStorage unavailable -- hidden for this page load only.
  }
};

// Undo "Remind me later" when she asks for the modal herself.
export const clearPinRemindLater = (userId) => {
  try {
    sessionStorage.removeItem(getRemindLaterKey(userId));
  } catch (e) {
    // sessionStorage unavailable -- nothing was stored.
  }
};

// Top-banner dismissal for the texting move, for this browser session only.
// Keyed by user AND banner state, so when the server moves the consultant to
// a new stage (a different message) the new banner shows even if she closed
// the previous one.
const BANNER_DISMISS_KEY_PREFIX = 'cp.dismissed.telnyxMigrationBanner';

const getBannerDismissKey = (userId, bannerState) => (
  `${BANNER_DISMISS_KEY_PREFIX}:${userId ?? 'unknown'}:${bannerState ?? 'default'}`
);

export const readMigrationBannerDismissed = (userId, bannerState) => {
  try {
    return sessionStorage.getItem(getBannerDismissKey(userId, bannerState)) === '1';
  } catch (e) {
    return false;
  }
};

export const writeMigrationBannerDismissed = (userId, bannerState) => {
  try {
    sessionStorage.setItem(getBannerDismissKey(userId, bannerState), '1');
  } catch (e) {
    // sessionStorage unavailable -- hidden until the next render resets it.
  }
};

// Banner headline and body as one line. The headline is written without a
// full stop (it doubles as a title), so add one when it lacks end
// punctuation: "A texting code is on its way. In the next day or two..."
export const bannerHeadlineSentence = (headline) => {
  const text = String(headline ?? '').trim();
  if (!text) {
    return '';
  }
  return /[.!?]$/.test(text) ? text : `${text}.`;
};
